import {TimeSettings} from '@guestapp/sdk';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import dayjs, {type Dayjs} from 'dayjs';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
const TIME_FORMAT = 'HH:mm:ss';
const SHORT_TIME_FORMAT = 'HH:mm';
const LABEL_TIME_FORMAT = 'HH:mm';

type CommonTimeSettings = Omit<TimeSettings, 'interval_unit'>;

const getMinMaxSettings = (timeSettings?: CommonTimeSettings) => {
  if (timeSettings) {
    return {
      min: dayjs(timeSettings.min_time, SHORT_TIME_FORMAT),
      max: dayjs(timeSettings.max_time, SHORT_TIME_FORMAT),
    };
  }

  return {
    min: dayjs('00:00', SHORT_TIME_FORMAT),
    max: dayjs('23:00', SHORT_TIME_FORMAT),
  };
};

const roundUp = (date: Dayjs) =>
  date.minute() === 0 ? date : date.add(1, 'hour').startOf('hour');

const buildHoursOptions = (timeSettings?: CommonTimeSettings) => {
  const hoursRange = timeSettings?.interval || 1;
  const settings = getMinMaxSettings(timeSettings);
  const min = roundUp(settings.min);
  const max = settings.max.startOf('hour');

  const timeOptions: {value: string; label: string}[] = [];

  let current = min.clone();
  while (current.isSameOrBefore(max)) {
    timeOptions.push({
      value: current.format(SHORT_TIME_FORMAT),
      label: current.format(LABEL_TIME_FORMAT),
    });
    current = current.add(hoursRange, 'hour');
  }

  return timeOptions;
};

const buildMinutesOptions = (timeSettings?: CommonTimeSettings) => {
  const minutesRange = timeSettings?.interval || 60;
  const {min, max} = getMinMaxSettings(timeSettings);

  const timeOptions: {value: string; label: string}[] = [];

  let current = min.clone();
  while (current.isSameOrBefore(max)) {
    timeOptions.push({
      value: current.format(TIME_FORMAT),
      label: current.format(LABEL_TIME_FORMAT),
    });
    current = current.add(minutesRange, 'minute');
  }

  return timeOptions;
};

export const buildOptions = (timeSettings?: TimeSettings) =>
  timeSettings?.interval_unit === 'H'
    ? buildHoursOptions(timeSettings)
    : buildMinutesOptions(timeSettings);
